import type { FC } from 'react';
import { Suspense, useMemo } from 'react';
import ReactModal from 'react-modal';
import { useTheme } from 'styled-components';

import { Button } from '../../Controls/Button/index.tsx';
import Icons from '../../Elements/Icons/index.ts';
import { Loader } from '../../Elements/Loader/index.tsx';
import { InfoTooltipIcon } from '../InfoTooltipIcon/index.tsx';
import { DEFAULT_MODAL_ZINDEX, MODAL_ANIMATION_DURATION_MS } from './constants.ts';
import { Header, Wrapper, StyledModal, Body, Footer, TitleWrapper, Title } from './styled.ts';
import type { ModalProps } from './types.ts';

const appEl = document.getElementById('root') || document.body;
ReactModal.setAppElement(appEl);
if (ReactModal.defaultStyles.overlay) {
  ReactModal.defaultStyles.overlay.zIndex = DEFAULT_MODAL_ZINDEX;
}

const ModalComponent: FC<ModalProps> = ({
  children,
  state,
  title,
  width = '700px',
  height = 'auto',
  shouldCloseOnOverlayClick = false,
  zIndex = DEFAULT_MODAL_ZINDEX,
  scroll,
  tooltip,
  ...props
}) => {
  const { colors } = useTheme();

  const modalStyles = useMemo<ReactModal.Styles>(() => ({ overlay: { zIndex, backgroundColor: 'rgba(0, 0, 0, 0.4)' } }), [zIndex]);

  return (
    <StyledModal
      isOpen={state.isOpen}
      onRequestClose={state.close}
      onAfterClose={state.afterClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      closeTimeoutMS={MODAL_ANIMATION_DURATION_MS}
      width={width}
      height={height}
      scroll={scroll}
      style={modalStyles}
      {...props}>
      <Suspense fallback={<Loader cover />}>
        <Wrapper data-test='modal-window'>
          {title ? (
            <Header>
              <TitleWrapper data-testid='modal-window-title'>
                <Title>{title}</Title>
                {tooltip && <InfoTooltipIcon tooltip={tooltip} />}
              </TitleWrapper>
              <Button ghost iconOnly onClick={state.close} data-testid='close-button-modal-window'>
                <Icons.Close size={24} color={colors.grey100_1} />
              </Button>
            </Header>
          ) : null}
          {children}
        </Wrapper>
      </Suspense>
    </StyledModal>
  );
};

export const Modal = Object.assign(ModalComponent, {
  Body,
  Footer,
});
